import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { classesApi, type CreateClassPayload } from '@/api/endpoints/classes'
import { useUI } from '@/stores/ui'
import { toLocalDateString } from '@/utils/date'
import TeacherPicker from '@/components/dashboard/TeacherPicker'

interface Props {
  open: boolean
  onClose: () => void
  onCreated?: () => void
}

export default function CreateClassModal({ open, onClose, onCreated }: Props) {
  const addToast = useUI((state) => state.addToast)
  const [name, setName] = useState('')
  const [location, setLocation] = useState('')
  const [notes, setNotes] = useState('')
  const [teacherIds, setTeacherIds] = useState<string[]>([])
  const [weeklyTimeDesc, setWeeklyTimeDesc] = useState('')
  const [startDate, setStartDate] = useState('')
  const [classHasStarted, setClassHasStarted] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    try {
      // Validate start date if provided
      if (startDate) {
        const selectedDate = new Date(startDate)
        const today = new Date()
        today.setHours(0, 0, 0, 0) // Reset time to start of day
        const twoYearsFromNow = new Date()
        twoYearsFromNow.setFullYear(twoYearsFromNow.getFullYear() + 2)
        
        if (classHasStarted) {
          // If class has already started, date must be in the past
          if (selectedDate >= today) {
            addToast({
              message: 'Start date must be in the past for classes that have already started',
              type: 'error',
            })
            setSubmitting(false)
            return
          }
        } else {
          // If class hasn't started, date must be in the future
          if (selectedDate < today) {
            addToast({
              message: 'Start date must be in the future',
              type: 'error',
            })
            setSubmitting(false)
            return
          }
          
          if (selectedDate > twoYearsFromNow) {
            addToast({
              message: 'Start date cannot be more than 2 years from today',
              type: 'error',
            })
            setSubmitting(false)
            return
          }
        }
      }
      
      const payload: CreateClassPayload = {
        name,
        address: location || undefined, // Backend expects 'address', not 'location'
        weekly_time_description: weeklyTimeDesc || undefined, // Backend expects 'weekly_time_description', not 'weekly_time_desc'
        start_date: startDate || undefined,
        notes: notes || undefined,
        teacher_ids: teacherIds,
      }
      await classesApi.create(payload)
      addToast({ message: 'Class created successfully', type: 'success' })
      // Reset form
      setName('')
      setLocation('')
      setNotes('')
      setTeacherIds([])
      setWeeklyTimeDesc('')
      setStartDate('')
      setClassHasStarted(false)
      onCreated?.()
      onClose()
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Failed to create class'
      addToast({
        message: errorMessage,
        type: 'error',
      })
      console.error('Class creation error:', error)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 pointer-events-none"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6 hover-raise pointer-events-auto"
            initial={{ scale: 0.96, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.96, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 260, damping: 22 }}
          >
            <h3 className="text-lg font-semibold mb-2">Create New Class</h3>
            <form onSubmit={submit} className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Class Name *</label>
                <input
                  className="input-field w-full focus:outline-none focus:ring-2 focus:ring-yellow-400/60 transition-shadow"
                  placeholder="e.g., First Communion Class"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Class Notes</label>
                <textarea
                  className="input-field w-full min-h-[70px] focus:outline-none focus:ring-2 focus:ring-yellow-400/60 transition-shadow"
                  placeholder="Notes that appear in the class (teachers can edit)"
                  value={notes}
                  maxLength={1023}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location/Address</label>
                <input
                  className="input-field w-full focus:outline-none focus:ring-2 focus:ring-yellow-400/60 transition-shadow"
                  placeholder="Class location or address"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Weekly Time Description</label>
                <input
                  className="input-field w-full focus:outline-none focus:ring-2 focus:ring-yellow-400/60 transition-shadow"
                  placeholder="e.g., Every Monday night at 6:30 PM"
                  value={weeklyTimeDesc}
                  onChange={(e) => setWeeklyTimeDesc(e.target.value)}
                />
              </div>
              <div>
                <label className="inline-flex items-center gap-2 mb-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-4 h-4 text-yellow-500 focus-ring rounded border-gray-300"
                    checked={classHasStarted}
                    onChange={(e) => {
                      setClassHasStarted(e.target.checked)
                      if (e.target.checked) {
                        // Clear start date when checking "already started"
                        setStartDate('')
                      }
                    }}
                  />
                  <span className="text-sm font-medium text-gray-700">Class has already started</span>
                </label>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date
                  {!classHasStarted && (
                    <span className="text-xs text-gray-500 ml-2">(Must be in the future, max 2 years from today)</span>
                  )}
                  {classHasStarted && (
                    <span className="text-xs text-gray-500 ml-2">(Must be in the past)</span>
                  )}
                </label>
                <input
                  type="date"
                  className="input-field w-full focus:outline-none focus:ring-2 focus:ring-yellow-400/60 transition-shadow"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  min={classHasStarted ? undefined : toLocalDateString(new Date())}
                  max={classHasStarted ? toLocalDateString(new Date()) : toLocalDateString(new Date(new Date().setFullYear(new Date().getFullYear() + 2)))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Teachers</label>
                <TeacherPicker value={teacherIds} onChange={setTeacherIds} />
                <p className="text-xs text-gray-500 mt-1">Class ID is generated automatically (CLASS + 8 digits) and never changes.</p>
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <button
                  type="button"
                  className="btn-secondary transition-transform duration-200 hover:scale-[1.02] active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
                  onClick={onClose}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary transition-transform duration-200 hover:scale-[1.02] active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
                  disabled={submitting}
                >
                  {submitting ? 'Creating…' : 'Create Class'}
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

